import React, { useState, useMemo } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useTranslation } from "@/lib/i18n";
import { useAgentsStore, useAgentViewMode, useToggleViewMode } from "@/lib/agents-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, LayoutGrid, List, SlidersHorizontal } from "lucide-react";
import { AgentCard } from "@/components/agents/AgentCard";
import { AgentTable } from "@/components/agents/AgentTable";
import { AgentModal } from "@/components/agents/AgentModal";
import { AgentQuickTest } from "@/components/agents/AgentQuickTest";
import { motion, AnimatePresence } from "framer-motion";
import type { Agent } from "@/lib/agents-store";
export default function AgentsPage() {
  const { t } = useTranslation();
  const agents = useAgentsStore((s) => s.agents);
  const viewMode = useAgentViewMode();
  const toggleViewMode = useToggleViewMode();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTestOpen, setIsTestOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | undefined>(undefined);
  const filteredAgents = useMemo(() => {
    return agents.filter((a) => {
      const matchesSearch = a.name.toLowerCase().includes(search.toLowerCase()) ||
                            a.role.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "all" || a.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [agents, search, statusFilter]);
  const openCreateModal = () => {
    setEditingAgent(undefined);
    setIsModalOpen(true);
  };
  const openEditModal = (agent: Agent) => {
    setEditingAgent(agent);
    setIsModalOpen(true);
  };
  const openTest = () => setIsTestOpen(true);
  return (
    <AppLayout container>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{t('agents_title')}</h1>
            <p className="text-muted-foreground mt-1">Deploy and manage your specialized AI workforce.</p>
          </div>
          <Button
            className="gap-2 h-11 px-6 bg-gradient-primary text-primary-foreground shadow-glow hover:scale-105 active:scale-95 transition-all"
            onClick={openCreateModal}
          >
            <Plus className="w-5 h-5" />
            {t('create_agent')}
          </Button>
        </div>
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-card/50 p-4 rounded-xl border border-white/5 backdrop-blur-sm">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('search_agents')}
              className="pl-10 bg-background/50"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px] bg-background/50">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filter_all')}</SelectItem>
                <SelectItem value="active">{t('filter_active')}</SelectItem>
                <SelectItem value="draft">{t('filter_draft')}</SelectItem>
                <SelectItem value="marketplace">{t('filter_marketplace')}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex border rounded-lg overflow-hidden shrink-0">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={toggleViewMode}
                className="rounded-none h-10 w-10"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="icon"
                onClick={toggleViewMode}
                className="rounded-none h-10 w-10"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        <div className="min-h-[400px]">
          <AnimatePresence mode="wait">
            {viewMode === 'grid' ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {filteredAgents.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} onOpenTest={openTest} onEdit={openEditModal} />
                ))}
              </motion.div>
            ) : (
              <motion.div key="table" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <AgentTable agents={filteredAgents} onEdit={openEditModal} onOpenTest={() => setIsTestOpen(true)} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <AgentModal open={isModalOpen} onOpenChange={setIsModalOpen} editAgent={editingAgent} />
        <AgentQuickTest open={isTestOpen} onOpenChange={setIsTestOpen} />
      </div>
    </AppLayout>
  );
}
